<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-50 to-white">
    <!-- Hero Section -->
    <div class="relative overflow-hidden">
      <!-- Hero background gradient pulse -->
      <div class="absolute inset-0 opacity-10">
        <div class="platform-bg"></div>
      </div>

      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="relative pt-20 pb-16 sm:pb-24 lg:pb-32">
          <!-- Logo/Brand Section -->
          <div class="text-center mb-8">
            <div class="flex justify-center items-center mb-4">
              <div
                class="w-16 h-16 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center mr-4"
              >
                <!-- ASAB icon -->
                <svg class="w-10 h-10 text-white" viewBox="0 0 100 100" fill="currentColor">
                  <circle cx="50" cy="50" r="8" />
                  <circle cx="25" cy="25" r="4" opacity="0.8" />
                  <circle cx="75" cy="25" r="4" opacity="0.8" />
                  <circle cx="25" cy="75" r="4" opacity="0.8" />
                  <circle cx="75" cy="75" r="4" opacity="0.8" />
                  <path
                    d="M50 42 L50 58 M42 50 L58 50 M32 32 L68 68 M32 68 L68 32"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
              </div>
              <div class="text-left">
                <h1 class="text-2xl font-bold text-gray-900">ASAB</h1>
                <p class="text-sm text-gray-600 font-medium">عَصَب</p>
                <p class="text-xs text-gray-500">Autonomous Self-Aware Bots</p>
              </div>
            </div>
          </div>

          <div class="text-center">
            <h1
              class="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl lg:text-6xl"
            >
              Your gateway to
              <span
                class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
              >
                agentic AI.
              </span>
            </h1>

            <p class="mt-6 max-w-3xl mx-auto text-lg text-gray-600 sm:text-xl">
              ASAB (عَصَب) helps teams build, deploy, and scale
              AI Agents Autonomously. Bring your own tools, plug into the models you already use,
              and stay on open standards.
            </p>

            <div class="mt-8 flex flex-col sm:flex-row justify-center gap-4">
              <router-link
                to="/auth/register"
                class="inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transform transition-all duration-200 hover:scale-105 shadow-lg"
              >
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                Start Building
              </router-link>
              <router-link
                to="/auth/login"
                class="inline-flex items-center justify-center px-8 py-4 border-2 border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200 hover:scale-105 shadow-md"
              >
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                  />
                </svg>
                Sign in
              </router-link>
            </div>

            <!-- Open standards strip -->
            <div class="mt-12">
              <p class="text-sm text-gray-500 mb-4">
                Open standards. No lock-in.
              </p>
              <div
                class="flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-sm text-gray-600"
              >
                <span class="font-semibold">A2A</span>
                <span aria-hidden="true">·</span>
                <span class="font-semibold">Model Context Protocol</span>
                <span aria-hidden="true">·</span>
                <span class="font-semibold">Compatible APIs</span>
                <span aria-hidden="true">·</span>
                <span class="font-semibold">OpenTelemetry</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dual Value Proposition Section -->
    <div class="py-16 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Agentic AI, end to end.
          </h2>
          <p class="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
            From prototype to production: build, integrate, and operate
            Agents without rewriting your stack.
          </p>
        </div>

        <div class="grid md:grid-cols-2 gap-12 items-center">
          <!-- Agents as a Service -->
          <div class="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8">
            <div class="flex items-center mb-6">
              <div
                class="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center mr-4"
              >
                <svg
                  class="w-7 h-7 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 class="text-2xl font-bold text-gray-900">Agents as a Service</h3>
            </div>
            <p class="text-gray-600 mb-6">
              Compose AI agents in minutes — no orchestrator to install, no scheduler to babysit.
              Author once, ship to production with built-in guardrails, structured outputs, and
              full execution traces.
            </p>
            <ul class="space-y-3">
              <li class="flex items-start">
                <svg
                  class="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-gray-700">Zero-infra deployment — prompt to running agent in minutes</span>
              </li>
              <li class="flex items-start">
                <svg
                  class="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-gray-700">Low-code agent platform — proven patterns built in</span>
              </li>
              <li class="flex items-start">
                <svg
                  class="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-gray-700">Guardrails, structured output, and per-step audit, out of the box</span>
              </li>
            </ul>
          </div>

          <!-- Open Tooling -->
          <div class="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8">
            <div class="flex items-center mb-6">
              <div
                class="w-12 h-12 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center mr-4"
              >
                <svg
                  class="w-7 h-7 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <h3 class="text-2xl font-bold text-gray-900">Open Tooling, Your Way</h3>
            </div>
            <p class="text-gray-600 mb-6">
              Bring your own tools — write them in any language, plug them into any agent. Native
              Model Context Protocol means the same tool runs in our platform, in Claude Desktop,
              or anywhere MCP is supported.
            </p>
            <ul class="space-y-3">
              <li class="flex items-start">
                <svg
                  class="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-gray-700">Bring your own tools — Rust, Python, or TypeScript</span>
              </li>
              <li class="flex items-start">
                <svg
                  class="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-gray-700">Native MCP — interop with the wider agentic AI ecosystem</span>
              </li>
              <li class="flex items-start">
                <svg
                  class="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-gray-700">Connect to your own APIs without writing glue code</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Core Features Section -->
    <div class="py-16 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-base text-blue-600 font-semibold tracking-wide uppercase">
            Platform capabilities
          </h2>
          <p class="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Everything you need to run agents in production.
          </p>
          <p class="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
            Model-agnostic, observability-first, governance-ready — out of the box.
          </p>
        </div>

        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <!-- Model-agnostic -->
          <div
            class="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300"
          >
            <div
              class="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mb-6"
            >
              <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-4">Model-agnostic by design</h3>
            <p class="text-gray-600 mb-4">
              Run any agent on any model. Switch providers in a config flag — no rewrites.
            </p>
            <ul class="text-sm text-gray-500 space-y-2">
              <li>• Plug in the model providers you already use</li>
              <li>• ASAB-models — self-healing failover across providers</li>
              <li>• Popular API compatibility — drop in without a rewrite</li>
            </ul>
          </div>

          <!-- Binding Networks -->
          <div
            class="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300"
          >
            <div
              class="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mb-6"
            >
              <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-4">Real observability</h3>
            <p class="text-gray-600 mb-4">
              Every step of every agent, traced. Export to your own backend or query in-app.
            </p>
            <ul class="text-sm text-gray-500 space-y-2">
              <li>• OpenTelemetry-native execution traces</li>
              <li>• Per-call cost + latency breakdown</li>
              <li>• Audit log of every tool invocation</li>
            </ul>
          </div>

          <!-- Strike Against Chaos -->
          <div
            class="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300"
          >
            <div
              class="w-12 h-12 bg-gradient-to-r from-red-500 to-pink-600 rounded-lg flex items-center justify-center mb-6"
            >
              <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-4">Org governance, built-in</h3>
            <p class="text-gray-600 mb-4">
              Per-organization quotas, credits, and roles — multi-tenant from day one.
            </p>
            <ul class="text-sm text-gray-500 space-y-2">
              <li>• Unified credit usage across models and tools</li>
              <li>• Per-org rate limits and concurrency caps</li>
              <li>• Fine-grained permissions and scoped API keys</li>
            </ul>
          </div>

          <!-- MCP gateway -->
          <div
            class="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300"
          >
            <div
              class="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center mb-6"
            >
              <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-4">MCP gateway</h3>
            <p class="text-gray-600 mb-4">
              Be both producer and consumer of the Model Context Protocol. Tools work everywhere.
            </p>
            <ul class="text-sm text-gray-500 space-y-2">
              <li>• Stream MCP from any agent in your workspace</li>
              <li>• Connect to external MCP servers in one click</li>
              <li>• Author once, run anywhere MCP is supported</li>
            </ul>
          </div>

          <!-- Living Evolution -->
          <div
            class="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300"
          >
            <div
              class="w-12 h-12 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-lg flex items-center justify-center mb-6"
            >
              <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-4">Search &amp; knowledge, included</h3>
            <p class="text-gray-600 mb-4">
              Web search and scraping endpoints out of the box — billed transparently.
            </p>
            <ul class="text-sm text-gray-500 space-y-2">
              <li>• Search, scrape, answer, and summarize APIs</li>
              <li>• Resilient — engineered for high availability</li>
              <li>• Transparent, predictable usage-based pricing</li>
            </ul>
          </div>

          <!-- Robust Infrastructure -->
          <div
            class="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300"
          >
            <div
              class="w-12 h-12 bg-gradient-to-r from-gray-700 to-gray-900 rounded-lg flex items-center justify-center mb-6"
            >
              <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-4">Enterprise-ready</h3>
            <p class="text-gray-600 mb-4">
              SSO, SCIM provisioning, and audit trails for the teams that need them.
            </p>
            <ul class="text-sm text-gray-500 space-y-2">
              <li>• SAML 2.0 SSO + SCIM provisioning</li>
              <li>• API keys with editable scopes</li>
              <li>• Compliance-grade audit log on every action</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Use Cases Section -->
    <div class="py-16 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-3xl font-extrabold text-gray-900 sm:text-4xl mb-4">
            Built for the way teams actually use AI
          </h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Whether you're shipping a customer-facing copilot or automating an internal workflow.
          </p>
        </div>

        <div class="grid lg:grid-cols-2 gap-12">
          <!-- Advanced Autonomous Deployment -->
          <div class="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
            <div class="flex items-center mb-6">
              <div
                class="w-12 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center mr-4"
              >
                <svg
                  class="w-7 h-7 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                  />
                </svg>
              </div>
              <h3 class="text-2xl font-bold text-gray-900">Customer-facing AI</h3>
            </div>
            <p class="text-gray-700 mb-6">
              Build copilots, support agents, and AI features your users interact with directly.
              Ship fast, scale safely, audit every step.
            </p>
            <div class="space-y-4">
              <div class="flex items-start">
                <svg
                  class="w-5 h-5 text-blue-600 mt-1 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <div>
                  <h4 class="font-semibold text-gray-900">Copilots in your product</h4>
                  <p class="text-gray-600 text-sm">
                    Embedded chat, inline assistance, smart actions
                  </p>
                </div>
              </div>
              <div class="flex items-start">
                <svg
                  class="w-5 h-5 text-blue-600 mt-1 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <div>
                  <h4 class="font-semibold text-gray-900">Support automation</h4>
                  <p class="text-gray-600 text-sm">
                    Triage, deflection, hand-off to humans with full context
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Internal automation -->
          <div class="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8">
            <div class="flex items-center mb-6">
              <div
                class="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center mr-4"
              >
                <svg
                  class="w-7 h-7 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <h3 class="text-2xl font-bold text-gray-900">Internal automation</h3>
            </div>
            <p class="text-gray-700 mb-6">
              Wire agents into your internal tooling. Automate data ops, run multi-step workflows,
              and connect to the systems your team already uses.
            </p>
            <div class="space-y-4">
              <div class="flex items-start">
                <svg
                  class="w-5 h-5 text-purple-600 mt-1 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <div>
                  <h4 class="font-semibold text-gray-900">Workflow automation</h4>
                  <p class="text-gray-600 text-sm">
                    Multi-step pipelines that combine LLMs with your APIs
                  </p>
                </div>
              </div>
              <div class="flex items-start">
                <svg
                  class="w-5 h-5 text-purple-600 mt-1 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <div>
                  <h4 class="font-semibold text-gray-900">Knowledge agents</h4>
                  <p class="text-gray-600 text-sm">
                    Search, summarize, and answer over your private data
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Technology Stack Section -->
    <div class="py-16 bg-gray-900">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-3xl font-extrabold text-white sm:text-4xl mb-4">
            Engineered for production
          </h2>
          <p class="text-xl text-gray-300 max-w-3xl mx-auto">
            A modern stack built to scale from your first prototype to real production traffic.
          </p>
        </div>

        <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div class="text-center">
            <div
              class="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl mx-auto mb-4 flex items-center justify-center"
            >
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white mb-2">Architecture</h3>
            <p class="text-gray-400 text-sm">
              Serverless agent infrastructure that scales with your traffic.
            </p>
          </div>

          <div class="text-center">
            <div
              class="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl mx-auto mb-4 flex items-center justify-center"
            >
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white mb-2">Open standards</h3>
            <p class="text-gray-400 text-sm">
              Speak the protocols the rest of the agent ecosystem speaks.
            </p>
          </div>

          <div class="text-center">
            <div
              class="w-16 h-16 bg-gradient-to-r from-red-500 to-pink-600 rounded-xl mx-auto mb-4 flex items-center justify-center"
            >
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white mb-2">Guardrails</h3>
            <p class="text-gray-400 text-sm">
              Validate inputs, outputs, and tool calls — automatically.
            </p>
          </div>

          <div class="text-center">
            <div
              class="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl mx-auto mb-4 flex items-center justify-center"
            >
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white mb-2">Observability</h3>
            <p class="text-gray-400 text-sm">
              Trace every step. Audit every action. Export it all.
            </p>
          </div>
        </div>

        <!-- Promise strip -->
        <div class="mt-16 grid md:grid-cols-3 gap-8 text-center">
          <div>
            <div class="text-2xl font-bold text-white mb-2">Production-ready</div>
            <div class="text-gray-400">Built for the long run</div>
          </div>
          <div>
            <div class="text-2xl font-bold text-white mb-2">Open by default</div>
            <div class="text-gray-400">No vendor lock-in, ever</div>
          </div>
          <div>
            <div class="text-2xl font-bold text-white mb-2">Secure by design</div>
            <div class="text-gray-400">Audit + access control built in</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pricing Section -->
    <div class="py-16 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-3xl font-extrabold text-gray-900 sm:text-4xl mb-4">
            Simple, predictable pricing
          </h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Start free. Pay for what you use. No surprises.
          </p>
        </div>

        <div class="grid lg:grid-cols-3 gap-8">
          <!-- Starter Plan -->
          <div class="bg-white rounded-2xl shadow-lg p-8">
            <div class="text-center mb-8">
              <h3 class="text-2xl font-bold text-gray-900 mb-2">Free</h3>
              <p class="text-gray-600 mb-4">For individuals exploring the platform</p>
              <div class="text-4xl font-bold text-gray-900 mb-2">$0</div>
              <p class="text-gray-500">Forever</p>
            </div>
            <ul class="space-y-4 mb-8">
              <li class="flex items-start">
                <svg
                  class="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-gray-700">Personal workspace</span>
              </li>
              <li class="flex items-start">
                <svg
                  class="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-gray-700">Up to 10 concurrent agent executions</span>
              </li>
              <li class="flex items-start">
                <svg
                  class="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-gray-700">$5 in usage credits to start</span>
              </li>
              <li class="flex items-start">
                <svg
                  class="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-gray-700">Community support</span>
              </li>
            </ul>
            <router-link
              to="/auth/register"
              class="block text-center w-full bg-gray-200 text-gray-800 py-3 px-6 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
            >
              Get started free
            </router-link>
          </div>

          <!-- Professional Plan -->
          <div
            class="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl shadow-xl p-8 relative border-2 border-blue-200"
          >
            <div class="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span
                class="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full text-sm font-semibold"
              >
                Most Popular
              </span>
            </div>
            <div class="text-center mb-8">
              <h3 class="text-2xl font-bold text-gray-900 mb-2">Pro</h3>
              <p class="text-gray-600 mb-4">For small teams shipping in production</p>
              <div class="text-4xl font-bold text-gray-900 mb-2">$50</div>
              <p class="text-gray-500">per month</p>
            </div>
            <ul class="space-y-4 mb-8">
              <li class="flex items-start">
                <svg
                  class="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-gray-700">Up to 100 concurrent agent executions</span>
              </li>
              <li class="flex items-start">
                <svg
                  class="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-gray-700">3 included team seats</span>
              </li>
              <li class="flex items-start">
                <svg
                  class="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-gray-700">Usage credits billed monthly</span>
              </li>
              <li class="flex items-start">
                <svg
                  class="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-gray-700">Priority email support</span>
              </li>
            </ul>
            <router-link
              to="/auth/register"
              class="block text-center w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105"
            >
              Start Pro
            </router-link>
          </div>

          <!-- Enterprise Plan -->
          <div class="bg-white rounded-2xl shadow-lg p-8">
            <div class="text-center mb-8">
              <h3 class="text-2xl font-bold text-gray-900 mb-2">Enterprise</h3>
              <p class="text-gray-600 mb-4">For organizations that need more control</p>
              <div class="text-4xl font-bold text-gray-900 mb-2">$100</div>
              <p class="text-gray-500">per month</p>
            </div>
            <ul class="space-y-4 mb-8">
              <li class="flex items-start">
                <svg
                  class="w-5 h-5 text-purple-500 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-gray-700">Up to 200 concurrent agent executions (request more)</span>
              </li>
              <li class="flex items-start">
                <svg
                  class="w-5 h-5 text-purple-500 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-gray-700">5 included team seats + SSO</span>
              </li>
              <li class="flex items-start">
                <svg
                  class="w-5 h-5 text-purple-500 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-gray-700">Priority scheduler</span>
              </li>
              <li class="flex items-start">
                <svg
                  class="w-5 h-5 text-purple-500 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-gray-700">Dedicated support Available</span>
              </li>
            </ul>
            <a
              href="mailto:support@asab.dev?subject=ASAB%20Enterprise%20enquiry"
              class="block text-center w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-800 transition-colors"
            >
              Contact sales
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Final CTA Section -->
    <div class="bg-gradient-to-r from-blue-600 to-purple-600">
      <div class="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:py-20 lg:px-8 text-center">
        <h2 class="text-3xl font-extrabold text-white sm:text-4xl mb-4">
          Get early access
        </h2>
        <p class="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
          We're in closed beta. Tell us about your use case and we'll reach out within
          1–3 business days.
        </p>
        <div class="flex flex-col sm:flex-row justify-center gap-4">
          <router-link
            to="/auth/register"
            class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-base font-medium rounded-lg text-blue-600 bg-white hover:bg-gray-50 transform transition-all duration-200 hover:scale-105"
          >
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            Request invite
          </router-link>
          <router-link
            to="/auth/login"
            class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-base font-medium rounded-lg text-white bg-transparent hover:bg-white hover:text-blue-600 transition-all duration-200"
          >
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
              />
            </svg>
            Sign in
          </router-link>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  // Hero background gradient pulse — defined in scoped <style> below
</script>

<style scoped>
  .platform-bg {
    background-image:
      radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 70%),
      radial-gradient(circle at 80% 20%, rgba(147, 51, 234, 0.1) 0%, transparent 70%),
      radial-gradient(circle at 40% 80%, rgba(59, 130, 246, 0.05) 0%, transparent 70%);
    animation: platform-pulse 4s ease-in-out infinite;
  }

  @keyframes platform-pulse {
    0%,
    100% {
      opacity: 0.1;
    }
    50% {
      opacity: 0.2;
    }
  }

  /* Hover animations for feature cards */
  .group:hover .platform-glow {
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
  }
</style>
